import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icons } from "assets";
import { routeConstant } from "shared/routes/routeConstant";
import { ResetPasswordService } from "shared/services/auth";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import deadLinkNotice from "./deadLinkNotice";
import { useFormik } from "formik";
import { ResetPasswordVS } from "shared/utils/validation";
import CustomInput from "shared/components/common/customInput";
import classNames from "classnames";
import CustomButton from "shared/components/common/customButton";
import styles from "../reset-password/style.module.scss";

// First-time password setup from the welcome email's token; no escape link, the account is unusable until it's set.
const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const tokenFromUrl = searchParams.get("token") || "";

  useEffect(() => {
    if (!tokenFromUrl) {
      navigate(routeConstant.login.path);
    }
  }, [tokenFromUrl, navigate]);

  const formik = useFormik({
    initialValues: {
      resetToken: tokenFromUrl,
      password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: ResetPasswordVS,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setSubmitting(true);
      try {
        const response = await ResetPasswordService({
          resetToken: values.resetToken,
          password: values.password,
          confirmPassword: values.confirmPassword,
        });

        if (response.status === 200 || response.status === 201) {
          toastMessage(
            "success",
            "Your password is set. You can now log in.",
          );
          setTimeout(() => {
            navigate(routeConstant.login.path);
          }, 2000);
        }
      } catch (error: any) {
        const notice = deadLinkNotice(error);
        if (notice) {
          toastMessage(notice.type, notice.message);
          navigate(routeConstant.login.path, { replace: true });
          return;
        }
        handleErrors(error);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const { handleChange, handleSubmit, values, touched, errors } = formik;

  return (
    <section
      className={classNames(
        "w-full flex flex-col items-center relative",
        styles.leftContainer,
      )}
    >
      <div className="flex items-center w-full justify-center m-auto">
        <form
          className={styles.formContainer}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          noValidate
        >
          <div className="flex flex-col gap-3 text-center">
            <h1 className={classNames(styles.title, "text-center")}>
              Set Your Password
            </h1>
            <p className={classNames(styles.subTitle, "text-center")}>
              Choose a password to finish setting up your account.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <CustomInput
              label="Password"
              placeholder="•••••••••••"
              required
              isPassword
              Icon={Icons.Lock}
              type="password"
              value={values.password}
              error={touched.password && errors.password ? errors.password : ""}
              onChange={handleChange("password")}
              customLabelStyle={styles.labelStyle}
            />

            <CustomInput
              label="Confirm Password"
              placeholder="•••••••••••"
              required
              isPassword
              Icon={Icons.Lock}
              type="password"
              value={values.confirmPassword}
              error={
                touched.confirmPassword && errors.confirmPassword
                  ? errors.confirmPassword
                  : ""
              }
              onChange={handleChange("confirmPassword")}
              customLabelStyle={styles.labelStyle}
            />

            <div>
              <CustomButton
                title="Set Password"
                containerStyle="w-full bg-blue maxHeighted_btn"
                type="submit"
                loading={loading}
                disabled={loading}
              />
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SetPassword;
